import { useEffect, useState } from "react";
import { toast } from "sonner";
import PublicShell from "@/components/PublicShell";
import { VideoSlot } from "@/components/VideoSlot";
import { api } from "@/lib/api";
import { Star } from "lucide-react";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [f, setF] = useState({ name: "", email: "", rating: 5, comment: "" });
  const [sending, setSending] = useState(false);

  const load = () => api.get("/reviews").then(r => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", f);
      toast.success("Thank you! Your review will appear once approved.");
      setF({ name: "", email: "", rating: 5, comment: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setSending(false); }
  };

  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <VideoSlot page="reviews" spot="top" className="mb-16" />
        <div className="overline text-mute mb-4">Client Words</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight mb-14 text-ink">The nicest part of the work.</h1>
        {reviews.length === 0 ? (
          <p className="text-mute mb-16">No reviews yet — be the first.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-8 mb-24">
            {reviews.map(r => (
              <div key={r.id} className="p-8 bg-cream-soft border border-ink-10" data-testid={`review-${r.id}`}>
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.id}-star-${i}`} size={14} fill="var(--brand-ink)" strokeWidth={0} />)}
                </div>
                <p className="font-serif text-xl leading-relaxed mb-6 text-ink-soft">&ldquo;{r.comment}&rdquo;</p>
                <div className="overline text-mute">— {r.name}</div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-ink-10 pt-14">
          <div className="overline text-mute mb-4">Share Yours</div>
          <h2 className="font-serif text-3xl md:text-4xl font-light mb-8 text-ink">Leave a review</h2>
          <form onSubmit={submit} className="space-y-6 max-w-2xl" data-testid="review-form">
            <div className="grid md:grid-cols-2 gap-6">
              <input required className="input-line" placeholder="Your name" data-testid="review-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
              <input required type="email" className="input-line" placeholder="Email (not shown)" data-testid="review-email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
            </div>
            <div>
              <div className="overline text-mute mb-2">Rating</div>
              <div className="flex gap-2" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setF({ ...f, rating: n })}
                    data-testid={`star-${n}`}
                    className="p-1">
                    <Star size={22} fill={n <= f.rating ? "var(--brand-ink)" : "transparent"} strokeWidth={n <= f.rating ? 0 : 1.5} color="var(--brand-ink)" />
                  </button>
                ))}
              </div>
            </div>
            <textarea required rows={5} className="input-line" placeholder="Your experience…" data-testid="review-comment" value={f.comment} onChange={e => setF({ ...f, comment: e.target.value })} />
            <button className="btn-ink" type="submit" disabled={sending} data-testid="review-submit">{sending ? "Sending…" : "Submit Review"}</button>
          </form>
        </div>
        <VideoSlot page="reviews" spot="bottom" className="mt-20" />
      </section>
    </PublicShell>
  );
}
